/**
 * Правила установки объектов на стену — общие для чертежа, развёрток и 3D.
 *
 * При перетаскивании предмет переходит на стены помещения под курсором и удерживается
 * в пределах чистой грани (`wallFixtureSpotInRoom`, `roomFaceSpans`).
 *
 * Привязки: вдоль стены — к 10 мм, по высоте — к типовым отметкам установки
 * (300 мм розетка, 900 мм выключатель и т. д.). Мышью в круглое число иначе
 * не попасть, а на чертеже розетки почти всегда стоят «в линию». `free` (Alt)
 * отключает привязки.
 *
 * Настенные объекты живут в разных слоях: электрика — в своём сторе, сантехника
 * и настенная мебель — в своих. Здесь же — куда положить новый и кто владеет
 * стоящим, чтобы ни холст, ни 3D об этом не думали. И замена типа выделенного объекта
 * из каталога — одна для чертежа и 3D (`replaceSelection`).
 */
import { CatalogAttach, getCatalogItem, isUpholstered, type CatalogItem } from "@/shared/catalog";
import {
	floorItemOwner,
	floorItemsStoreFor,
	useFixturesStore,
	wallFixtureOwner,
	wallFixturesStoreFor,
} from "@/shared/store/layers";
import { removeFixture, removeFloorItem } from "@/shared/store/actions";
import { useProjectStore } from "@/shared/store";
import { SelectionKind, type Selection } from "@/widgets/editor";
import { add, dist, mul, norm, pointInPolygon, sub, type Pt } from "@/shared/lib/geometry";
import {
	detectRooms,
	roomFaceSpans,
	roomOfFace,
	type Room,
	type RoomFaceSpan,
} from "@/widgets/plan";
import {
	defaultResizeAnchor,
	itemDimensions,
	type CatalogRef,
	type Fixture,
	type FloorItem,
	type WallGraph,
} from "@/shared/plan";

/** Шаг привязки вдоль стены, мм. */
export const WALL_STEP = 10;

/**
 * Типовые высоты установки, мм. К ним притягивается объект при перетаскивании:
 * на чертеже розетки почти всегда стоят «в линию».
 */
const HEIGHTS = [100, 300, 600, 900, 1100, 1500, 1800, 2100];

/** Радиус притягивания к типовой высоте, мм. */
const HEIGHT_SNAP = 60;

/** Положение вдоль стены с шагом привязки. */
export const snapAlongWall = (value: number, free: boolean) =>
	free ? Math.round(value) : Math.round(value / WALL_STEP) * WALL_STEP;

/** Высота установки, притянутая к ближайшей типовой отметке. */
export const snapHeight = (value: number, free: boolean): number => {
	if (free) return Math.round(value);
	let best = value;
	let bestDistance = HEIGHT_SNAP;
	for (const height of HEIGHTS) {
		const distance = Math.abs(height - value);
		if (distance < bestDistance) {
			bestDistance = distance;
			best = height;
		}
	}
	return best === value ? snapAlongWall(value, false) : best;
};

/** Поставить объект на стену в слой его раздела; null — стены нет или она слишком короткая. */
export const addWallFixture = (
	wallId: string,
	ref: CatalogRef,
	offset: number,
	elevation: number,
	side: 1 | -1,
): string | null => {
	const store = wallFixturesStoreFor(ref.catalog);
	if (store) return store.getState().addFixture(wallId, ref, offset, elevation, side);
	return useFixturesStore.getState().addFixture(wallId, ref, offset, elevation, side);
};

/** Стоящий на стене объект по id — из какого бы слоя он ни был. */
export const findWallFixture = (id: string): Fixture | null =>
	useFixturesStore.getState().items[id] ?? wallFixtureOwner(id)?.getState().items[id] ?? null;

/** Сдвинуть объект вдоль его стены и по высоте. */
export const setWallFixturePlacement = (id: string, offset: number, elevation: number) => {
	if (id in useFixturesStore.getState().items) {
		useFixturesStore.getState().setFixturePlacement(id, offset, elevation);
		return;
	}
	wallFixtureOwner(id)?.getState().setFixturePlacement(id, offset, elevation);
};

/** Перенести объект на другую стену или грань — и сразу на нужное место и высоту. */
export const moveWallFixture = (
	id: string,
	wallId: string,
	side: 1 | -1,
	offset: number,
	elevation: number,
) => {
	const fixture = findWallFixture(id);
	if (!fixture) return;
	if (fixture.wallId !== wallId || fixture.side !== side) {
		if (id in useFixturesStore.getState().items) {
			useFixturesStore.getState().setFixtureWall(id, wallId, offset, side);
		} else {
			wallFixtureOwner(id)?.getState().setFixtureWall(id, wallId, offset, side);
		}
	}
	setWallFixturePlacement(id, offset, elevation);
};

/** Стор, в котором объект этого раздела висит на стене: свой у сантехники и мебели, иначе электрика. */
const storeOfRef = (ref: CatalogRef) => wallFixturesStoreFor(ref.catalog) ?? useFixturesStore;
/** Стор, в котором висит стоящий объект. */
const storeOfFixture = (id: string) =>
	id in useFixturesStore.getState().items ? useFixturesStore : wallFixtureOwner(id);

/**
 * Заменить объект на стене другим типом на том же месте. Высота: если пользователь её
 * не трогал, новый объект встаёт на свою типовую (розетку меняют на выключатель —
 * он уходит на 900), а выставленную вручную сохраняем. Тип из другого слоя (розетку
 * на полотенцесушитель) переносим в его слой: прежний объект уходит вместе с кабелями
 * или трубами — к новому они всё равно не подходят. Возвращает id объекта после замены.
 */
export const replaceWallFixture = (id: string, ref: CatalogRef): string | null => {
	const fixture = findWallFixture(id);
	const info = getCatalogItem(ref);
	if (!fixture || !info) return null;
	const before = getCatalogItem(fixture.ref);
	const elevation =
		!before || fixture.elevation === before.elevation ? info.elevation : fixture.elevation;
	const target = storeOfRef(ref);
	if (storeOfFixture(id) === target) {
		target.getState().updateFixture(id, {
			ref,
			elevation,
			width: undefined,
			height: undefined,
			depth: undefined,
		});
		return id;
	}
	removeFixture(id);
	return addWallFixture(fixture.wallId, ref, fixture.offset, elevation, fixture.side);
};

/**
 * Заменить предмет на полу другим типом на том же месте и с тем же поворотом. Предмет
 * живёт в слое своего раздела (`floorItemsStoreFor`): кровать, заменённая на унитаз,
 * переезжает из мебели в сантехнику. Личные габариты, цвет и высоту прежнего предмета
 * новому не переносим — они подбирались под другой предмет. Спинка остаётся на месте:
 * кровать у стены и после замены стоит к ней вплотную. Возвращает id после замены.
 */
export const replaceFloorItem = (id: string, ref: CatalogRef): string | null => {
	const owner = floorItemOwner(id);
	const current = owner?.getState().items[id];
	const before = current ? getCatalogItem(current.ref) : null;
	const after = getCatalogItem(ref);
	if (!owner || !current || !after) return null;
	const at = before ? backKeepingCenter(current, before, after) : { x: current.x, y: current.y };
	const target = floorItemsStoreFor(ref.catalog);
	if (owner === target) {
		target.getState().updateFloorItem(id, {
			ref,
			x: at.x,
			y: at.y,
			width: undefined,
			height: undefined,
			depth: undefined,
			color: undefined,
			elevation: undefined,
			// Обивка переходит на другую модель мягкой мебели: диван сменили, ткань та же
			fabric: isUpholstered(after) ? current.fabric : undefined,
		});
		return id;
	}
	removeFloorItem(id);
	return target.getState().addFloorItem(ref, at.x, at.y, current.rotation);
};

/** Где спинка предмета по оси глубины от центра; null — предмет смотрит вбок, спинки нет. */
const backOffset = (info: CatalogItem, depth: number) => {
	const anchor = defaultResizeAnchor(info, "depth");
	return anchor === "start" ? -depth / 2 : anchor === "end" ? depth / 2 : null;
};

/**
 * Центр нового предмета, при котором его спинка стоит там же, где спинка прежнего.
 * Глубина у нового своя: кровать 2150 вместо 1880, — и с прежним центром изголовье
 * ушло бы в стену или отошло от неё. Спинку берём по той же логике, что и при смене
 * глубины (`defaultResizeAnchor`); у предмета, который смотрит вбок, центр не трогаем.
 */
const backKeepingCenter = (item: FloorItem, before: CatalogItem, after: CatalogItem) => {
	const oldBack = backOffset(before, itemDimensions(item, before).depth);
	const newBack = backOffset(after, after.depth);
	if (oldBack === null || newBack === null) return { x: item.x, y: item.y };
	// Сдвиг центра вдоль оси глубины предмета — тем же поворотом, что у символа на плане
	const shift = oldBack - newBack;
	const angle = (item.rotation * Math.PI) / 180;
	return {
		x: Math.round(item.x - shift * Math.sin(angle)),
		y: Math.round(item.y + shift * Math.cos(angle)),
	};
};

/** Какой вид объектов заменяет карточка каталога при этом выделении. */
const ATTACH_OF_SELECTION: Partial<Record<SelectionKind, CatalogAttach>> = {
	[SelectionKind.Item]: CatalogAttach.Everywhere,
	[SelectionKind.Fixture]: CatalogAttach.OnlyWall,
	[SelectionKind.Light]: CatalogAttach.Ceiling,
};

/** Заменит ли карточка каталога выделенный объект: вид карточки совпадает с видом выделенного. */
export const canReplaceSelection = (selection: Selection | null, ref: CatalogRef): boolean => {
	const info = getCatalogItem(ref);
	return selection !== null && info !== null && ATTACH_OF_SELECTION[selection.kind] === info.attach;
};

/**
 * Клик по карточке каталога при выделенном объекте того же вида — замена его типа:
 * выделил кровать, выбрал в каталоге другую — она встала на место прежней. Одна запись
 * в истории. Возвращает новое выделение (id мог смениться при переезде в другой слой)
 * или null, если заменять нечего — тогда каталог ставит новый объект, как обычно.
 */
export const replaceSelection = (
	selection: Selection | null,
	ref: CatalogRef,
): Selection | null => {
	if (!selection || !canReplaceSelection(selection, ref)) return null;
	useProjectStore.getState().pushHistory();
	if (selection.kind === SelectionKind.Item) {
		const id = replaceFloorItem(selection.id, ref);
		return id ? { kind: SelectionKind.Item, id } : null;
	}
	if (selection.kind === SelectionKind.Fixture) {
		const id = replaceWallFixture(selection.id, ref);
		return id ? { kind: SelectionKind.Fixture, id } : null;
	}
	useFixturesStore.getState().updateCeilingItem(selection.id, { ref });
	return selection;
};

/** Комната, в которую смотрит предмет на стене; null — он снаружи квартиры. */
export const fixtureRoom = (graph: WallGraph, rooms: Room[], fixture: Fixture): Room | null =>
	roomOfFace(graph, rooms, { wallId: fixture.wallId, side: fixture.side });

/**
 * Отступ вдоль стены, при котором предмет шириной `width` целиком на грани комнаты.
 * Грань уже предмета — ставим посередине грани.
 */
export const clampToSpan = (span: RoomFaceSpan, offset: number, width: number): number => {
	const lo = span.from + width / 2;
	const hi = span.to - width / 2;
	return lo <= hi ? Math.min(hi, Math.max(lo, offset)) : (span.from + span.to) / 2;
};

/** Место на стене: какая стена, какой стороной и где вдоль неё. */
export interface WallSpot {
	wallId: string;
	side: 1 | -1;
	offset: number;
}

/**
 * Место на стенах комнаты `room`, ближайшее к точке плана: стена своей комнаты, сторона —
 * в комнату, отступ — в пределах её чистой грани, с привязкой (`free` — без привязки).
 */
export const wallSpotInRoom = (
	graph: WallGraph,
	room: Room,
	point: Pt,
	width: number,
	free: boolean,
): WallSpot | null => {
	let best: { spot: WallSpot; distance: number } | null = null;
	for (const span of roomFaceSpans(graph, room)) {
		const wall = graph.walls[span.wallId];
		const a = wall && graph.nodes[wall.a];
		const b = wall && graph.nodes[wall.b];
		if (!wall || !a || !b) continue;
		const along = norm(sub(b, a));
		const raw = (point.x - a.x) * along.x + (point.y - a.y) * along.y;
		const onFace = Math.min(span.to, Math.max(span.from, raw));
		const distance = dist(point, add(a, mul(along, onFace)));
		if (best && distance >= best.distance) continue;
		const offset = clampToSpan(span, snapAlongWall(raw, free), width);
		best = { spot: { wallId: span.wallId, side: span.side, offset }, distance };
	}
	return best?.spot ?? null;
};

/**
 * Куда встанет перетаскиваемый предмет на стене, если курсор в точке плана `point`:
 * на ближайшую стену комнаты под курсором; вне неё используется текущая комната.
 * Если комнату не определить, холст ведёт предмет на ближайшую стену.
 */
export const wallFixtureSpotInRoom = (id: string, point: Pt, free: boolean): WallSpot | null => {
	const fixture = findWallFixture(id);
	const info = fixture && getCatalogItem(fixture.ref);
	if (!fixture || !info) return null;
	const graph = useProjectStore.getState().project.layers.walls;
	const rooms = detectRooms(graph);
	const room =
		rooms.find((candidate) => pointInPolygon(point, candidate.inner)) ??
		fixtureRoom(graph, rooms, fixture);
	if (!room) return null;
	return wallSpotInRoom(graph, room, point, itemDimensions(fixture, info).width, free);
};
